import { EnvError } from './errors.js';
import { testResets } from './testing-state.js';

export interface EnvLoadOptions {
  /** Explicit producer-owned URL. Never inferred from the host document. */
  url: string;
  fetch?: typeof globalThis.fetch;
  /** Positive finite milliseconds, at most 2^31 - 1. Defaults to 10 seconds. */
  timeoutMs?: number;
  signal?: AbortSignal;
}

export interface EnvInitializeOptions<Output> {
  /** Normalize validated data once, before publishing. The result is not parsed again. */
  prepare?: (parsed: Output) => Output;
}

export interface EnvClient<Output> {
  initialize(raw: unknown, options?: EnvInitializeOptions<Output>): Readonly<Output>;
  load(options: EnvLoadOptions): Promise<Readonly<Output>>;
  ready(): Promise<Readonly<Output>>;
  get<K extends keyof Output>(key: K): Output[K];
  require<K extends keyof Output>(key: K): NonNullable<Output[K]>;
  all(): Readonly<Output>;
}

type Attempt<Output> = {
  source:
    | { kind: 'inline'; raw: unknown; prepare?: (parsed: Output) => Output }
    | { kind: 'url'; url: string };
  promise?: Promise<Readonly<Output>>;
  snapshot?: Readonly<Output>;
  error?: unknown;
  failed?: boolean;
  cancel?: () => void;
  cancelled?: boolean;
};

// JSON objects and arrays are frozen recursively. Zod owns the parsed copy.
function freezeSnapshot<T>(value: T, seen = new WeakSet<object>()): Readonly<T> {
  if (value && typeof value === 'object' && !seen.has(value)) {
    seen.add(value);
    Object.values(value).forEach((child) => freezeSnapshot(child, seen));
    Object.freeze(value);
  }
  return value;
}

function notReady(): EnvError {
  return new EnvError('not-ready', 'env not initialized; call initialize() or await load() first');
}

function sourceConflict(): EnvError {
  return new EnvError('source-conflict', 'this env handle already has a different source');
}

/** Internal factory; each declaration owns exactly one attempt, never a shared singleton. */
export function createEnvClient<Output>(parse: (raw: unknown) => Output): EnvClient<Output> {
  let attempt: Attempt<Output> | undefined;

  function all(): Readonly<Output> {
    if (attempt?.failed) throw attempt.error;
    if (!attempt?.snapshot) throw notReady();
    return attempt.snapshot;
  }

  const client: EnvClient<Output> = {
    initialize(raw, { prepare } = {}) {
      if (attempt) {
        if (
          attempt.source.kind !== 'inline' ||
          attempt.source.raw !== raw ||
          attempt.source.prepare !== prepare
        )
          throw sourceConflict();
        return all();
      }
      attempt = { source: { kind: 'inline', raw, prepare } };
      try {
        const parsed = parse(raw);
        let prepared = parsed;
        try {
          if (prepare) prepared = prepare(parsed);
        } catch {
          throw new EnvError('validation', 'config preparation failed');
        }
        attempt.snapshot = freezeSnapshot(prepared);
        attempt.promise = Promise.resolve(attempt.snapshot);
        return attempt.snapshot;
      } catch (error) {
        attempt.failed = true;
        attempt.error = error;
        attempt.promise = Promise.reject(error);
        // initialize throws synchronously; ready() still returns the original rejection.
        void attempt.promise.catch(() => {});
        throw error;
      }
    },
    load(options) {
      const retrying =
        attempt?.failed && attempt.source.kind === 'url' && attempt.source.url === options.url;
      if (attempt && !retrying) {
        if (attempt.source.kind !== 'url' || attempt.source.url !== options.url) {
          return Promise.reject(sourceConflict());
        }
        // The first call owns transport options, including timeout and cancellation.
        return attempt.promise!;
      }
      const sourceOptions = { ...options };
      const current: Attempt<Output> = { source: { kind: 'url', url: sourceOptions.url } };
      attempt = current;
      // Defer transport until after the attempt promise is assigned (also safe for reentry).
      current.promise = Promise.resolve()
        .then(() => fetchRaw(sourceOptions, current))
        .then((raw) => {
          current.snapshot = freezeSnapshot(parse(raw));
          return current.snapshot;
        })
        .catch((error: unknown) => {
          current.failed = true;
          current.error = error;
          throw error;
        });
      return current.promise;
    },
    ready() {
      return attempt?.promise ?? Promise.reject(notReady());
    },
    get(key) {
      return all()[key];
    },
    require(key) {
      const value = all()[key];
      if (value === undefined || value === null) {
        throw new EnvError('missing-key', `required env key "${String(key)}" is missing`);
      }
      return value as NonNullable<Output[typeof key]>;
    },
    all,
  };
  testResets.set(client, () => {
    if (attempt) attempt.cancelled = true;
    attempt?.cancel?.();
    attempt = undefined;
  });
  return client;
}

function fetchRaw<Output>(options: EnvLoadOptions, attempt: Attempt<Output>): Promise<unknown> {
  const { url, signal, timeoutMs = 10_000 } = options;
  if (
    typeof url !== 'string' ||
    !url.trim() ||
    !Number.isFinite(timeoutMs) ||
    timeoutMs <= 0 ||
    timeoutMs > 2_147_483_647
  ) {
    throw new EnvError('invalid-options', 'load requires a URL and a positive finite timeoutMs');
  }
  const controller = new AbortController();
  return new Promise((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout>;
    function finish(error: unknown, raw?: unknown): void {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      if (error) reject(error);
      else resolve(raw);
    }
    function cancel(code: 'aborted' | 'timeout'): void {
      finish(
        new EnvError(code, code === 'timeout' ? 'config load timed out' : 'config load aborted'),
      );
      controller.abort();
    }
    function abort(): void {
      cancel('aborted');
    }
    attempt.cancel = abort;
    if (signal?.aborted || attempt.cancelled) {
      abort();
      return;
    }
    signal?.addEventListener('abort', abort, { once: true });
    timer = setTimeout(() => cancel('timeout'), timeoutMs);
    // Race the complete response+JSON operation. Even a custom fetch that ignores abort is bounded.
    void (async () => {
      let response: Response;
      try {
        const fetcher = options.fetch ?? globalThis.fetch;
        response = await fetcher(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          signal: controller.signal,
        });
      } catch {
        throw new EnvError('network', 'config request failed');
      }
      if (!response.ok)
        throw new EnvError('http', `config request failed: HTTP ${response.status}`);
      try {
        return await response.json();
      } catch {
        throw new EnvError('json', 'config response is not valid JSON');
      }
    })().then((raw) => finish(undefined, raw), finish);
  });
}
